import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
// REISOR_STAGE22P1A_DRIVER_RUNTIME_I18N_CLEANUP_V1

function normalizeLegalLang(lang) {
  const raw = String(lang || "ru").toLowerCase();
  if (raw.startsWith("uz")) return "uz";
  if (raw.startsWith("tr")) return "tr";
  return "ru";
}

// REISOR_STAGE97_BILLING_LEGAL_ACCESS_V1
export function getPaidAccessRulesUrl(lang = "ru") {
  return `https://backend.cargo-platform.online/api/public/legal/paid-access/print?scope=driver&lang=${encodeURIComponent(normalizeLegalLang(lang))}`;
}

function extractDocHtml(raw) {
  const text = String(raw || "");
  if (!text.trim()) return "";

  const hasHtml = /<html[\s>]/i.test(text);
  if (hasHtml) return text;

  return `<!doctype html>
<html lang="ru">
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1" />
  <style>
    body {
      margin: 0;
      padding: 20px;
      font-family: Arial, sans-serif;
      line-height: 1.6;
      color: #111827;
      background: #ffffff;
    }
    h1,h2,h3 { line-height: 1.25; }
    img { max-width: 100%; height: auto; }
    table { width: 100%; border-collapse: collapse; }
  </style>
</head>
<body>
${text}
</body>
</html>`;
}

export default function LegalDocsModal({ title, url, onClose }) {
  const { t } = useTranslation();
  const finalUrl = useMemo(() => String(url || "").trim(), [url]);
  const [loading, setLoading] = useState(true);
  const [docHtml, setDocHtml] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    const controller = new AbortController();

    async function load() {
      setLoading(true);
      setError("");
      setDocHtml("");

      if (!finalUrl) {
        setLoading(false);
        setError(t("legalDocs.noUrl", { defaultValue: "Не указан адрес документа." }));
        return;
      }

      if (typeof navigator !== "undefined" && navigator.onLine === false) {
        setLoading(false);
        setError(t("legalDocs.offline", { defaultValue: "Документ временно недоступен офлайн. Подключите интернет и попробуйте снова." }));
        return;
      }

      try {
        const r = await fetch(finalUrl, {
          method: "GET",
          credentials: "omit",
          signal: controller.signal,
          headers: {
            "Accept": "text/html,application/xhtml+xml"
          }
        });

        if (!r.ok) {
          throw new Error(`HTTP_${r.status}`);
        }

        const raw = await r.text();
        const html = extractDocHtml(raw);

        if (!html.trim()) {
          throw new Error("EMPTY_DOCUMENT");
        }

        setDocHtml(html);
        setError("");
      } catch (e) {
        if (controller.signal.aborted) return;
        setError(t("legalDocs.loadFailed", { defaultValue: "Не удалось загрузить документ. Проверьте интернет-соединение и попробуйте снова." }));
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    }

    load();

    return () => controller.abort();
  }, [finalUrl]);

  return (
    <div
      className="fixed inset-0 bg-black/70 flex items-center justify-center px-4"
      style={{ zIndex: 12080 }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: "920px",
          height: "86vh",
          background: "#0f172a",
          border: "1px solid #334155",
          borderRadius: "18px",
          color: "white",
          boxShadow: "0 20px 60px rgba(0,0,0,0.45)",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: "12px",
            padding: "14px 16px",
            borderBottom: "1px solid #334155",
            background: "rgba(30, 41, 59, 0.95)",
            flexShrink: 0,
          }}
        >
          <div
            style={{
              fontSize: "18px",
              fontWeight: 700,
              lineHeight: 1.2,
              paddingRight: "10px",
              flex: "1 1 220px",
              minWidth: 0,
            }}
          >
            {title || t("legalDocs.document", { defaultValue: "Документ" })}
          </div>

          <div style={{ display: "flex", gap: "8px", flexShrink: 0, flexWrap: "wrap", justifyContent: "flex-end" }}>
            <button
              type="button"
              onClick={() => {
                setLoading(true);
                setError("");
                setDocHtml("");
                setTimeout(() => {
                  try {
                    window.dispatchEvent(new Event("legal-doc-reload"));
                  } catch {}
                }, 0);
              }}
              style={{
                padding: "10px 12px",
                borderRadius: "10px",
                border: "1px solid #475569",
                background: "#1e293b",
                color: "white",
                cursor: "pointer",
                fontSize: "14px",
                minWidth: "118px",
              }}
            >
              {t("common.refresh", { defaultValue: "Обновить" })}
            </button>

            <button
              type="button"
              onClick={onClose}
              style={{
                padding: "10px 12px",
                borderRadius: "10px",
                border: "none",
                background: "#3b82f6",
                color: "white",
                cursor: "pointer",
                fontSize: "14px",
                fontWeight: 600,
              }}
            >
              {t("common.close", { defaultValue: "Закрыть" })}
            </button>
          </div>
        </div>

        <LegalDocContent
          t={t}
          title={title}
          finalUrl={finalUrl}
          loading={loading}
          docHtml={docHtml}
          error={error}
          setLoading={setLoading}
          setDocHtml={setDocHtml}
          setError={setError}
        />
      </div>
    </div>
  );
}

function LegalDocContent({
  t,
  title,
  finalUrl,
  loading,
  docHtml,
  error,
  setLoading,
  setDocHtml,
  setError,
}) {
  useEffect(() => {
    const reload = async () => {
      setLoading(true);
      setError("");
      setDocHtml("");

      if (!finalUrl) {
        setLoading(false);
        setError(t("legalDocs.noUrl", { defaultValue: "Не указан адрес документа." }));
        return;
      }

      if (typeof navigator !== "undefined" && navigator.onLine === false) {
        setLoading(false);
        setError(t("legalDocs.offline", { defaultValue: "Документ временно недоступен офлайн. Подключите интернет и попробуйте снова." }));
        return;
      }

      try {
        const r = await fetch(finalUrl, {
          method: "GET",
          credentials: "omit",
          headers: {
            "Accept": "text/html,application/xhtml+xml"
          }
        });

        if (!r.ok) throw new Error(`HTTP_${r.status}`);

        const raw = await r.text();
        const html = extractDocHtml(raw);
        if (!html.trim()) throw new Error("EMPTY_DOCUMENT");

        setDocHtml(html);
        setError("");
      } catch {
        setError(t("legalDocs.loadFailed", { defaultValue: "Не удалось загрузить документ. Проверьте интернет-соединение и попробуйте снова." }));
      } finally {
        setLoading(false);
      }
    };

    const handler = () => reload();
    window.addEventListener("legal-doc-reload", handler);
    return () => window.removeEventListener("legal-doc-reload", handler);
  }, [finalUrl, setDocHtml, setError, setLoading]);

  return (
    <div style={{ flex: 1, minHeight: 0, padding: "16px" }}>
      {loading ? (
        <div
          style={{
            height: "100%",
            borderRadius: "16px",
            border: "1px solid #334155",
            background: "#111827",
            color: "#cbd5e1",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            textAlign: "center",
            padding: "24px",
            fontSize: "18px",
            fontWeight: 600,
          }}
        >
          {t("legalDocs.loading", { defaultValue: "Загрузка документа…" })}
        </div>
      ) : error ? (
        <div
          style={{
            height: "100%",
            borderRadius: "16px",
            border: "1px solid #334155",
            background: "#111827",
            color: "#cbd5e1",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            textAlign: "center",
            padding: "24px",
          }}
        >
          <div style={{ maxWidth: "560px" }}>
            <div style={{ fontSize: "28px", fontWeight: 800, color: "white", marginBottom: "12px" }}>
              {title || t("legalDocs.document", { defaultValue: "Документ" })}
            </div>
            <div style={{ fontSize: "18px", lineHeight: 1.6 }}>
              {error}
            </div>
          </div>
        </div>
      ) : (
        <div
          style={{
            width: "100%",
            height: "100%",
            overflow: "auto",
            border: "none",
            borderRadius: "16px",
            background: "white",
            color: "#111827",
            padding: "18px",
            boxSizing: "border-box",
            WebkitOverflowScrolling: "touch",
          }}
          dangerouslySetInnerHTML={{ __html: docHtml }}
        />
      )}
    </div>
  );
}
